import format from 'date-fns/format'

import { formatStudyDuration } from '@/core/helpers/educationHelpers'
import { camelCaseToSnakeCase } from '@/core/helpers/dataHelpers'
import { parseDate } from '@/core/helpers/dateTimeHelper'

const PROJECT_DATE_FORMAT = 'MMM yyyy'

const projectDateTime = dateStr => {
  const parsed = parseDate(dateStr)
  return parsed ? parsed.getTime() : null
}

/**
 * Sorts candidate projects for company profile: ongoing projects (no end date) appear first,
 * followed by completed projects by end date descending; tie-break with start date descending.
 *
 * @param {Object[]} projects - Raw project objects from the candidate store
 * @returns {Object[]}
 */
export const sortProjectsByEndDate = projects => {
  if (!projects?.length) return projects || []

  return [...projects].sort((a, b) => {
    const endA = projectDateTime(a.end_date)
    const endB = projectDateTime(b.end_date)
    const hasEndA = endA != null
    const hasEndB = endB != null

    if (!hasEndA && hasEndB) return -1
    if (hasEndA && !hasEndB) return 1
    if (hasEndA && hasEndB && endA !== endB) return endB - endA

    const startA = projectDateTime(a.start_date)
    const startB = projectDateTime(b.start_date)
    if (startA != null && startB != null && startA !== startB) return startB - startA
    if (startA != null && startB == null) return -1
    if (startA == null && startB != null) return 1

    return 0
  })
}

/**
 * Label for the project date row (requires a start date per PRO-3081).
 *
 * @param {string|null|undefined} startDate
 * @param {string|null|undefined} endDate
 * @returns {string|null}
 */
export const formatProjectDateLabel = (startDate, endDate) => {
  if (!startDate) return null

  const start = parseDate(startDate)
  if (!start) return null

  const startStr = format(start, PROJECT_DATE_FORMAT)

  if (!endDate) return `${startStr} - Present`

  const end = parseDate(endDate)
  if (!end) return startStr

  return `${startStr} - ${format(end, PROJECT_DATE_FORMAT)}`
}

export const computeEducation = educationArr =>
  educationArr.map(education => {
    const { start_year: startYear, graduation_year: graduationYear, university, ...rest } = education
    return {
      ...rest,
      university,
      startYear,
      graduationYear,
    }
  })

export const buildEducation = ({ id, university, degree, startYear, graduationYear, description }) => ({
  id,
  institution: university,
  degree,
  studyDuration: formatStudyDuration(startYear, graduationYear, 'MMMM yyyy'),
  isMissingEducationInfo: Boolean(!startYear || !graduationYear || !university || !degree),
  description,
})

export const sortEducation = (itemA, itemB) => {
  if (itemA.graduationYear > itemB.graduationYear) return -1
  if (itemA.graduationYear < itemB.graduationYear) return 1
  return 0
}

export const formatComprehensiveLocation = camelCasedLocation => {
  const snakeCasedLocation = camelCaseToSnakeCase(camelCasedLocation)

  snakeCasedLocation.country_iso2_code = snakeCasedLocation.country_iso2_code?.toLowerCase()
  snakeCasedLocation.location_category = snakeCasedLocation.location_category?.toLowerCase()
  snakeCasedLocation.type = snakeCasedLocation.type?.toLowerCase()

  return snakeCasedLocation
}
